import React, { useMemo } from "react";
import { notification, Typography } from "antd";

import { SectionTitle } from "./styledComponents";
import ActionLogDrawerAsync from "./ActionLogDrawerAsync";
import { FibuFinalCheckStatus } from "../../api/graphql/schema.generated";
import { IUser, IUserDetails, FibuFinalCheck } from "../../types";
import EditableCustomerAttribute, {
  EditType,
} from "./EditableCustomerAttribute";
import { useKontaxUserContext } from "../contexts/KontaxUserContext";

const FibuFinalCheckComponent = ({
  fibuFinalChecks,
  user,
  editUser,
  inlineLabel,
}: {
  fibuFinalChecks: FibuFinalCheck[];
  user: IUser;
  editUser: (email: string, userDetails: IUserDetails) => Promise<void>;
  // Compact one-line variant with this label; default is the Tax Home layout.
  inlineLabel?: string;
}) => {
  const RESET = "-- RESET --";

  const texts = useMemo(
    () =>
      fibuFinalChecks.reduce((all, vatYearSetting) => {
        const { year, id } = vatYearSetting;
        return {
          ...all,
          [String(id)]: `Year: ${year}`,
        };
      }, {}),
    [fibuFinalChecks]
  );

  const ids = useMemo(
    () => fibuFinalChecks.map(({ id }) => String(id)),
    [fibuFinalChecks]
  );

  const actionLog = (
    <ActionLogDrawerAsync
      title="Action log"
      modelName="fibu_final_check"
      recordIds={ids}
      subTexts={texts}
      small
    />
  );

  const renderFibuFinalCheck = (
    user: IUser,
    editUser: (email: string, userDetails: IUserDetails) => Promise<void>
  ) =>
    fibuFinalChecks?.map((fibuFinalCheck) => (
      <EditableCustomerAttribute
        key={fibuFinalCheck.year}
        label={inlineLabel ?? `Fibu final check status ${fibuFinalCheck.year}`}
        inline={!!inlineLabel}
        changeLog={inlineLabel ? actionLog : undefined}
        initialValue={fibuFinalCheck.status}
        editType={EditType.Select}
        options={{
          Statuses: Object.values(FibuFinalCheckStatus),
          "Reset status (WARNING: it's irreversible)": [RESET],
        }}
        onSaveHandler={async (selection: string) => {
          const status: FibuFinalCheckStatus | null =
            selection !== RESET ? (selection as FibuFinalCheckStatus) : null;
          try {
            await editUser(user.email, {
              fibuFinalChecks: [
                {
                  status,
                  year: fibuFinalCheck.year,
                },
              ],
            });
          } catch (error) {
            if ((error as Error).message === "Forbidden") {
              notification.error({
                message:
                  "Fibu status 'done' kann nicht gesetzt werden, weil die Buchhaltung noch nicht vollständig gebucht ist.",
              });
            }
          }
        }}
      />
    ));

  if (inlineLabel) {
    return <>{renderFibuFinalCheck(user, editUser)}</>;
  }

  return (
    <>
      <SectionTitle>
        <span>Fibu Status</span>
        <ActionLogDrawerAsync
          title="Action log"
          modelName="fibu_final_check"
          recordIds={ids}
          subTexts={texts}
          small
        />
      </SectionTitle>
      {renderFibuFinalCheck(user, editUser)}
    </>
  );
};

const yearNotAvailableHint = (year: number, availableYears: number[]) => {
  const firstYear = Math.min(...availableYears);
  const lastYear = Math.max(...availableYears);
  const range =
    firstYear === lastYear ? `${firstYear}` : `${firstYear}\u2013${lastYear}`;

  return `Für ${year} gibt es keinen Fibu-Status. Verfügbar: ${range}.`;
};

/*
 * Why the block is empty. Each branch states only what the data actually
 * shows: naming a cause we have not checked is worse than saying nothing,
 * because the operator then goes and "fixes" a field that was never the
 * problem.
 */
const noYearsHint = (firstTaxDeclarationYear?: number | null) => {
  if (!firstTaxDeclarationYear) {
    return "Kein Fibu-Status verfügbar: \u201EErste Steuererklärung\u201C ist für diesen Mandanten nicht gesetzt.";
  }

  const currentYear = new Date().getFullYear();
  if (firstTaxDeclarationYear >= currentYear) {
    return `Kein Fibu-Status verfügbar: \u201EErste Steuererklärung\u201C (${firstTaxDeclarationYear}) liegt nicht vor dem laufenden Jahr.`;
  }

  return `Kein Fibu-Status verfügbar, obwohl \u201EErste Steuererklärung\u201C auf ${firstTaxDeclarationYear} steht.`;
};

const FibuFinalCheckHint = ({
  text,
  inlineLabel,
}: {
  text: string;
  inlineLabel?: string;
}) =>
  inlineLabel ? (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "240px 1fr",
        gridGap: 8,
        padding: "2px 0",
      }}
    >
      <Typography.Text type="secondary">{inlineLabel}</Typography.Text>
      <Typography.Text type="secondary">{text}</Typography.Text>
    </div>
  ) : (
    <>
      <SectionTitle>
        <span>Fibu Status</span>
      </SectionTitle>
      <Typography.Text type="secondary">{text}</Typography.Text>
    </>
  );

export const UserFibuFinalCheck = ({
  year,
  inlineLabel,
}: {
  year: number;
  // Compact one-line variant (label column + value); default is unchanged.
  inlineLabel?: string;
}) => {
  const { user, editUser } = useKontaxUserContext();

  if (!user) return null;

  const { fibuFinalChecks, firstTaxDeclarationYear } = user;
  const fibuFinalCheck = fibuFinalChecks.find((entry) => entry.year === year);

  if (fibuFinalCheck) {
    return (
      <FibuFinalCheckComponent
        fibuFinalChecks={[fibuFinalCheck]}
        user={user}
        editUser={async (_, userData) => editUser(userData)}
        inlineLabel={inlineLabel}
      />
    );
  }

  /*
   * The backend serializes years from "Erste Steuererklärung" up to, but
   * excluding, the current year. The year selector offers a wider range, so
   * an empty block has two distinct shapes: some years exist but not the
   * selected one, or no years exist at all.
   */
  return (
    <FibuFinalCheckHint
      inlineLabel={inlineLabel}
      text={
        fibuFinalChecks.length
          ? yearNotAvailableHint(
              year,
              fibuFinalChecks.map((entry) => entry.year)
            )
          : noYearsHint(firstTaxDeclarationYear)
      }
    />
  );
};

export default FibuFinalCheckComponent;
